import { logInfo } from '../../utils'
import { RecapResponse, RecapType } from '../../types/recap'
import { SyncData, ToReset } from '../../types/sync'
import { useTaskProfiles } from '../../store/useTaskProfiles'
import { RecapResponsesModel } from '../../models/RecapReponsesModel'
import { Notify } from 'quasar'

export function recapResponsesSync() {
  function filterResponses(
    responses: RecapResponse[],
    isStoreActive: boolean,
    type: RecapType = 'task'
  ): { filtered: RecapResponse[]; duplicates: RecapResponse[] } {
    if (isStoreActive) {
      // Never return anything if there is an ongoing store visit
      // We don't want to sync anything that's in-progress
      return {
        filtered: [],
        duplicates: [],
      }
    }
    const duplicateResponses: RecapResponse[] = []
    const filteredResponses = responses.filter((response, index) => {
      let isFirst = false
      if (type === 'task') {
        // Make sure that this is the first response for this task
        isFirst =
          responses.findIndex(
            (res) => res.task_id && String(response.task_id!) === String(res.task_id!)
          ) === index
      } else if (type === 'venue') {
        // Make sure that this is the first response for this store AND this recap definition
        isFirst =
          responses.findIndex(
            (res) =>
              res.store_id &&
              String(response.store_id!) === String(res.store_id!) &&
              String(response.events_recaps_id) === String(res.events_recaps_id)
          ) === index
      } else {
        // The Neptune app does not support Event (or other) recaps
        // It's already false from above, but I wanted to make explicit note of it here
        isFirst = false
      }
      // If this isn't the first response, we have duplicates. Log it and keep track of them
      if (!isFirst) {
        logInfo('error', 'Duplicate recap response', 'Duplicate recap response', {
          task_id: response.task_id,
          store_id: response.store_id,
          response,
          responses,
        })
        // Keep track of duplicate responses
        duplicateResponses.push(response)
      } else {
        // If we're keeping this response, make sure there are no duplicated field responses (NPUB-1172)
        // This shouldn't happen after the fix for this hits production, but it's possible there are reps with bad data already
        // This should fix that (and keep it from happening again)
        const existingFieldInfo: { instance_id: string; repeatingIndex: number }[] = []
        for (let formIndex = 0; formIndex < response.form_responses.length; formIndex++) {
          const formResponse = response.form_responses[formIndex]
          const removeIndices: number[] = []
          for (let fieldIndex = 0; fieldIndex < formResponse.field_responses.length; fieldIndex++) {
            const fieldResponse = formResponse.field_responses[fieldIndex]
            if (
              !existingFieldInfo.find(
                (item) =>
                  String(item.instance_id) === String(fieldResponse.events_custom_instances_id) &&
                  item.repeatingIndex === formIndex
              )
            ) {
              existingFieldInfo.push({
                instance_id: String(fieldResponse.events_custom_instances_id),
                repeatingIndex: formIndex,
              })
            } else {
              removeIndices.push(fieldIndex)
            }
          }
          removeIndices.forEach((index) => {
            formResponse.field_responses.splice(index, 1)
          })
        }
      }
      return isFirst
    })
    return {
      filtered: filteredResponses,
      duplicates: duplicateResponses,
    }
  }

  /**
   * Prevent any tasks from being synced to the portal that have been flagged as invalid or duplicates.
   * As of writing, the possible cases are:
   *   1. Task recap response without a task ID attached (could happen if an old response hasn't been deleted properly)
   *   2. Duplicate task recap response
   * In the future the cases may expand (e.g. tasks missing images that should require one) but for now this is all we're concerned about
   *
   * @param syncData gathered sync data
   * @param toReset tuples of task IDs and/or recap responses to remove data for (found when getting images for the response, see case 1 above and `recapImagesSync.ts`)
   * @param duplicateResponses array of responses that are duplicates (case 2 above, found from the `filterResponses` function in this file)
   */
  async function resetTaskRecapData(
    syncData: SyncData,
    toReset: ToReset[],
    duplicateResponses: RecapResponse[]
  ): Promise<void> {
    // Gather all the reset information into one array
    const duplicateResponseResets: ToReset[] = duplicateResponses.map((response) => ({
      task_id: response.task_id,
      response,
    }))
    let allResets: ToReset[] = []
    allResets = allResets.concat(toReset)
    allResets = allResets.concat(duplicateResponseResets)
    const { resetTask, triggerTaskDataRef } = useTaskProfiles()
    for (const reset of toReset) {
      const taskId = reset.task_id ?? reset.response.task_id
      if (taskId) {
        // Reset everything related to this task.
        // This also handles resetting recap data in the store
        await resetTask(taskId, true)

        // Remove the task from the syncData object
        if (syncData.updated_tasks) {
          const matchingIndex = syncData.updated_tasks.findIndex(
            // In the sync data, it's just `id` instead of `task_id`
            (task) => task.id === reset.task_id
          )
          if (matchingIndex > -1) {
            syncData.updated_tasks.splice(matchingIndex, 1)
          }
        }
      }

      // If the reset has a response, we also need to remove it
      if (reset.response) {
        // If there was no task ID, the recap response hasn't been removed from the device yet, so do that now
        if (!taskId) {
          RecapResponsesModel()
            .removeByTaskId(reset.response.task_id!)
            .catch((err) =>
              console.error(
                `Error removing recap responses for task ID ${reset.response.task_id}: ${err}`
              )
            )
        }

        // Now remove the response data from the sync data
        const matchingIndex = syncData.recap_responses!.findIndex(
          (res) =>
            res.events_recaps_id === reset.response.events_recaps_id &&
            res.task_id === reset.response.task_id
        )
        if (matchingIndex >= 0) {
          syncData.recap_responses!.splice(matchingIndex, 1)
        }
      }
    }

    // If we reset any tasks, update them now
    // We skipped updates earlier because updating all the tasks
    // every time we reset a single one is extremely costly
    if (allResets.length) {
      await triggerTaskDataRef()

      const taskIds = allResets.map((reset) => reset.task_id).filter((id) => !!id)

      // Check the task ID length here because there could be a case where a recap response without a task was wiped
      if (taskIds && taskIds.length) {
        Notify.create({
          type: 'negative',
          timeout: 0,
          actions: [
            {
              icon: 'close',
              color: 'white',
              // for individual action (button):
              'aria-label': 'Dismiss',
            },
          ],
          multiLine: true,
          position: 'bottom-left',
          message:
            taskIds.length +
            ' Task' +
            (taskIds.length > 1 ? 's' : '') +
            ' from the last store visit were reset due to missing required image data. Surveys for these tasks will need to be completed again. The tasks were: ' +
            taskIds.join(', '),
        })
      }
    }
  }

  return {
    filterResponses,
    resetTaskRecapData,
  }
}
